import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BoobleModalService } from 'booble-modal';
import { Subscription, switchMap } from 'rxjs';
import {
  CreateOrderDto,
  CreateOrderInboundDto,
} from 'src/app/domain/models/dto/create-order.dto';
import { CreateOrderConfirmQuotationComponent } from 'src/app/pages/orders/shared/components/create-order/create-order-confirm-quotation/create-order-confirm-quotation.component';
import { CreateOrderConfirmComponent } from 'src/app/pages/orders/shared/components/create-order/create-order-confirm/create-order-confirm.component';
import { OrdersService } from '../../services/orders.service';
import { FinalitiesDto } from 'src/app/domain/models/dto/finalities.dto';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CountryDto } from 'src/app/domain/models/dto/country.dto';
import { MerchantService } from 'src/app/core/merchant.service';
import { TenantGeneralService } from 'src/app/core/tenant-general.service';

@Component({
  selector: 'app-create-order',
  templateUrl: './create-order.component.html',
  styleUrls: ['./create-order.component.scss'],
})
export class CreateOrderComponent implements OnInit, OnDestroy {
  @Input() rType;
  boobleModalData: string;
  step = 1;
  finality: FinalitiesDto;
  currency: string;
  country: CountryDto;
  loading: boolean = false;
  hasDocumentStep = false;
  createOrder$?: Subscription;
  createOrderInbound$?: Subscription;
  isMobile = false;
  createOrderModel?: CreateOrderDto;
  createOrderInboundModel?: CreateOrderInboundDto;
  merchant: string = '';
  warrenGlobalOnly: boolean = false;

  @ViewChild('goUp', { static: false }) goUp: ElementRef;

  constructor(
    private modalService: BoobleModalService,
    private orderService: OrdersService,
    private bottomSheet: MatBottomSheet,
    private merchantService: MerchantService,
    private tenantService: TenantGeneralService
  ) {}

  ngOnDestroy(): void {
    this.createOrder$?.unsubscribe();
    this.createOrderInbound$?.unsubscribe();
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 820 ? true : false;
    this.merchant = this.merchantService.getUserMerchant();

    this.warrenGlobalOnly =
      this.merchant == this.tenantService.WARREN_GLOBAL_NAME;

    if (this.isMobile) {
      this.boobleModalData = this.rType;
    }
  }

  closeModal() {
    if (this.isMobile) {
      this.bottomSheet.dismiss();
    } else {
      this.modalService.dismiss(null);
    }
  }

  finalitySelectedHandler(event) {
    this.finality = event;
    if (this.warrenGlobalOnly) {
      this.hasDocumentStep = false;
    } else {
      this.hasDocumentStep = !!this.finality.invoiceNeed;
    }

    this.step += 1;
    if (this.isMobile) {
      this.scrollToTop();
    }
  }

  currencySelectedHandler(event) {
    this.currency = event;
    this.step += 1;
  }

  beneficiarySaveHandler(event: CreateOrderDto) {
    event.finalityId = this.finality.id;
    if (this.hasDocumentStep) {
      this.createOrderModel = event;
      this.step += 1;
      if (this.isMobile) {
        this.scrollToTop();
      }
    } else {
      this.createOrderModel = event;
      this.createOrder(event);
    }
  }

  senderSaveHandler(event: CreateOrderInboundDto) {
    this.createOrderInboundModel = event;
    this.step += 1;
  }

  countryHandler(event: CountryDto) {
    this.country = event;
  }

  goBackHandler() {
    this.step -= 1;
  }

  sendFileHandler({ file, diNumber, diProtocol }) {
    this.loading = true;
    this.createOrder$ = this.orderService
      .validateUploadInvoiceFileOutbound(1, file, diNumber, diProtocol)
      .pipe(
        switchMap((resultValidate) =>
          this.orderService.createOutboundOrder(this.createOrderModel)
        ),
        switchMap((orderResult) =>
          this.orderService.uploadInvoiceFileOutbound(
            orderResult.data.id,
            file,
            diNumber,
            diProtocol
          )
        )
      )
      .subscribe({
        next: (res) => {
          if (this.isMobile) {
            this.bottomSheet.dismiss();
          }
          this.orderService.updateOrderView();
          this.openConfirmModal(1);
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
        },
      });
  }

  private createOrder(event: CreateOrderDto) {
    this.loading = true;
    this.createOrder$ = this.orderService.createOutboundOrder(event).subscribe({
      next: (res) => {
        if (this.isMobile) {
          this.bottomSheet.dismiss();
        }
        if (this.finality.analysisNeed) {
          this.openConfirmModal(1);
        } else {
          this.modalService.present(CreateOrderConfirmQuotationComponent, {
            ...this.createOrderModel,
            orderId: res.data.id,
            orderType: 1,
            finality: res.data.finality,
            country: res.data.receiver.country,
          });
        }
        this.orderService.updateOrderView();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  createOrderInbound() {
    this.loading = true;
    this.createOrderInbound$ = this.orderService
      .createNewInboundOrder(this.createOrderInboundModel)
      .subscribe({
        next: (res) => {
          if (this.isMobile) {
            this.bottomSheet.dismiss();
          }
          this.orderService.updateOrderView();
          this.openConfirmModal(0);
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
        },
      });
  }

  private scrollToTop() {
    if (this.goUp && this.goUp.nativeElement) {
      this.goUp.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  private openConfirmModal(rType: number) {
    this.modalService.present(CreateOrderConfirmComponent, rType);
  }
}
